import { useState, useRef, useEffect } from "react";
import { Badge, Modal, Form } from "antd";
import BellIcon from "@Assets/icons/bell.svg";
import UserAvatar from "@Assets/images/placeholderImage.png";
import ArrowDownIcon from "@Assets/icons/arrow-down.svg";
import useAuthStore from "@Store/authStore";
import { useNavigate } from "react-router-dom";
import { NavigationRoutes } from "@Navigation/NavigationRoutes";
import LogoutModalIcon from "@Assets/icons/logout-modal.svg";
import ConfirmationModal from "@Components/Modal/ConfirmationModal";
import Notifications from "../Notifications/Notifications";
import { useUserProfile } from "@Pages/AppScreens/UserManagement/Queries";
import ChangePasswordModal from "./ChangePasswordModal";
import NotificationContainer from "../Notifications/NotificationContainer";

const AppHeader = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { removeUserAuthentication } = useAuthStore();

  const { notificationCount } = NotificationContainer({ isOpen: false });

  const handleLogout = () => {
    removeUserAuthentication();
    navigate(NavigationRoutes.AUTH_ROUTES.LOGIN);
    setShowLogoutModal(false);
  };

  const { data: profileDetails, isLoading: isProfileLoading } =
    useUserProfile();
  const user = profileDetails?.body?.user;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="flex items-center gap-4 px-2">
      {/* User Profile */}
      <div className="flex !items-center gap-3">
        <div className="flex justify-center items-center">
          <div className="flex items-center h-10">
            <Notifications>
              <Badge
                count={notificationCount?.data?.unseenCount || 0}
                size="small"
                offset={[0, 0]}
                className="app-header-badge"
              >
                <div className="w-10 h-10 flex items-center justify-center border border-[#E9E9E9] rounded-[10px] cursor-pointer bg-white hover:bg-gray-50">
                  <BellIcon />
                </div>
              </Badge>
            </Notifications>
          </div>
        </div>

        {isProfileLoading ? (
          <div className="flex items-center gap-3 animate-pulse">
            <div className="w-[2.5rem] h-[2.5rem] rounded-full bg-gray-200" />
            <div className="flex flex-col gap-1">
              <span className="h-4 w-28 rounded bg-gray-200" />
              <span className="h-3 w-16 rounded bg-gray-100" />
            </div>
            <div className="w-3 h-3 rounded bg-gray-200" />
          </div>
        ) : (
          <>
            <div>
              <img
                src={user?.imageUrl || UserAvatar}
                className="w-[2.5rem] h-[2.5rem] rounded-full object-cover"
                alt="User Avatar"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold !text-app-primary !text-base leading-normal font-inter">
                {user?.firstName || "Admin"}
              </span>
              {/* <span className="!text-sm leading-normal !text-app-light font-inter">
                  Super Admin
                </span> */}
            </div>
            <div className="relative" ref={dropdownRef}>
              <div
                className="cursor-pointer"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <ArrowDownIcon className="w-4 h-4" />
              </div>

              {/* Dropdown Menu */}
              {showDropdown && (
                <div className="absolute right-0 mt-7 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <p
                      onClick={() =>navigate(NavigationRoutes.DASHBOARD_ROUTES.PROFILE_MANAGEMENT)}
                      className="w-full cursor-pointer text-left px-4 py-4 text-sm text-gray-700 hover:bg-[#FEF6F6] transition-colors"
                    >
                      Profile Management
                    </p>
                    <p
                      onClick={() => {
                        setShowLogoutModal(true);
                        setShowDropdown(false);
                      }}
                      className="w-full cursor-pointer text-left px-4 py-4 text-sm text-gray-700 hover:bg-[#FEF6F6] transition-colors"
                    >
                      Logout
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Modal
        open={showChangePasswordModal}
        onCancel={() => setShowChangePasswordModal(false)}
        className=""
        footer={null}
        centered
      >
        <ChangePasswordModal
          onClose={() => setShowChangePasswordModal(false)}
        />
      </Modal>

      <ConfirmationModal
        open={showLogoutModal}
        title="Logout"
        content="Are you sure you want to logout?"
        icon={
          <span className="w-24 h-24 inline-flex items-center justify-center ">
            <span className="w-24 h-24">
              <LogoutModalIcon />
            </span>
          </span>
        }
        onCancel={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        actionButtons={[
          {
            label: "Cancel",
            onClick: () => setShowLogoutModal(false),
            variant: "cancel",
          },
          {
            label: "Logout",
            onClick: handleLogout,
            variant: "danger",
          },
        ]}
      />
    </div>
  );
};

export default AppHeader;
